import { useEffect, useRef } from "react";

class Blob {
  constructor(index, total, blobSize) {
    const positions = [
      [0.08, 0.5], [0.5, 0.2], [0.92, 0.5], [0.15, 0.8],
      [0.85, 0.3], [0.3, 0.65], [0.7, 0.75], [0.5, 0.5],
    ];
    const [bx, by] = positions[index % positions.length];
    this.cx = bx + (Math.random() - 0.5) * 0.15;
    this.cy = by + (Math.random() - 0.5) * 0.15;
    this.numPts = 6 + Math.floor(Math.random() * 4);
    this.baseRadius = blobSize * (0.6 + Math.random() * 0.8);
    this.noiseOffsets = Array.from({ length: this.numPts }, () => Math.random() * 100);
    this.noiseSpeeds  = Array.from({ length: this.numPts }, () => 0.3 + Math.random() * 0.7);
    this.noiseAmps    = Array.from({ length: this.numPts }, () => 0.25 + Math.random() * 0.45);
    this.lineWidth = 0.5 + (index / (total - 1 || 1)) * 0.5;
    this.driftX = (Math.random() - 0.5) * 0.00008;
    this.driftY = (Math.random() - 0.5) * 0.00008;
  }

  getRadius(i, t, speed) {
    const phase = this.noiseOffsets[i] + t * this.noiseSpeeds[i] * speed;
    return this.baseRadius * (
      1 + Math.sin(phase) * this.noiseAmps[i]
        + Math.sin(phase * 1.7 + 0.9) * this.noiseAmps[i] * 0.4
    );
  }

  draw(ctx, canvas, t, speed, lineColor, opacity) {
    const w = canvas.width, h = canvas.height;
    const cx = this.cx * w, cy = this.cy * h;
    const n = this.numPts;

    const pts = Array.from({ length: n }, (_, i) => {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      const rad = this.getRadius(i, t, speed);
      return { x: cx + Math.cos(angle) * rad, y: cy + Math.sin(angle) * rad };
    });

    ctx.beginPath();
    for (let i = 0; i < n; i++) {
      const p0 = pts[(i - 1 + n) % n];
      const p1 = pts[i];
      const p2 = pts[(i + 1) % n];
      const p3 = pts[(i + 2) % n];
      const tension = 0.4;
      const cp1x = p1.x + (p2.x - p0.x) * tension;
      const cp1y = p1.y + (p2.y - p0.y) * tension;
      const cp2x = p2.x - (p3.x - p1.x) * tension;
      const cp2y = p2.y - (p3.y - p1.y) * tension;
      if (i === 0) ctx.moveTo(p1.x, p1.y);
      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
    }
    ctx.closePath();

    const [r, g, b] = [
      parseInt(lineColor.slice(1, 3), 16),
      parseInt(lineColor.slice(3, 5), 16),
      parseInt(lineColor.slice(5, 7), 16),
    ];
    ctx.strokeStyle = `rgba(${r},${g},${b},${opacity})`;
    ctx.lineWidth = this.lineWidth;
    ctx.stroke();

    this.cx += this.driftX;
    this.cy += this.driftY;
  }
}

export default function BlobBackground({
  bgColor="transparent",
    lineColor="#000000",
    speed=1,
    blobSize=300,
    blobCount=6,
    opacity=0.12,
  children,
}) {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const stateRef = useRef({ blobs: [], t: 0, animId: null });

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    const ctx = canvas.getContext("2d");
    const state = stateRef.current;

    const resize = () => {
      canvas.width = wrapper.clientWidth;
      canvas.height = wrapper.clientHeight;
    };

    state.blobs = Array.from({ length: blobCount }, (_, i) => new Blob(i, blobCount, blobSize));

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      state.t += 0.012;
      state.blobs.forEach(b => b.draw(ctx, canvas, state.t, speed, lineColor, opacity));
      state.animId = requestAnimationFrame(animate);
    };

    resize();
    window.addEventListener("resize", resize);
    animate();

    return () => {
      cancelAnimationFrame(state.animId);
      window.removeEventListener("resize", resize);
    };
  }, [bgColor, lineColor, speed, blobSize, blobCount, opacity]);

  return (
    <div
      ref={wrapperRef}
      style={{ position: "relative", width: "100%", minHeight: "100vh", background: bgColor, overflow: "hidden" }}
    >
      <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
      <div style={{ position: "relative", zIndex: 10 }}>{children}</div>
    </div>
  );

}